<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>{{ groupData['label'] ?? '' }} Notification Group</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container v-if="!readOnly">
                <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                         density="compact" variant="tonal">
                    <p v-for="error in errors">{{ error }}</p>
                </v-alert>
                <v-row>
                    <v-col cols="12">
                        <v-text-field v-model="groupData['label']"
                                      :rules="[vRules.required]"
                                      autofocus density="compact" label="Label"
                        >
                            <template v-slot:append>
                                <v-btn :disabled="!groupData['label']" variant="outlined" @click="storeGroup">Save
                                </v-btn>
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-container>

            <v-container v-if="groupData['id']">
                <v-row>
                    <v-col class="px-5 d-flex justify-space-between" cols="12">
                        <h3 class="text-left">User Recipients</h3>
                        <v-btn v-if="!readOnly" color="black" size="small" @click="createRecipient(0)">Add Recipient</v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-table height="200">
                            <thead>
                            <tr>
                                <th>User</th>
                                <th>App</th>
                                <th>Sms</th>
                                <th>Email</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="user in users" v-if="users.length" :key="user.id" class="text-left">
                                <td>
                                    {{ user.first_name }} {{ user.last_name }}
                                    <div v-if="user.email" class="text-caption text-grey">{{ user.email }}</div>
                                </td>
                                <td>
                                    <v-icon :icon="user?.app_enabled?'mdi-check':'mdi-close'"></v-icon>
                                </td>
                                <td>
                                    <v-icon :icon="user?.sms_enabled?'mdi-check':'mdi-close'"></v-icon>
                                </td>
                                <td>
                                    <v-icon :icon="user?.email_enabled?'mdi-check':'mdi-close'"></v-icon>
                                </td>
                                <td>
                                    <v-btn-group v-if="!readOnly" density="compact" divided variant="outlined">
                                        <v-btn :disabled="editDialogSwitch" class="text-warning"
                                               prepend-icon="mdi-pencil" size="x-small" @click="editRecipient(user)"/>
                                        <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                               @click="deleteRecipient(user)"/>
                                    </v-btn-group>
                                    <span v-else class="text-grey">-</span>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="5">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else class="text-center" colspan="5">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="px-5 d-flex justify-space-between" cols="12">
                        <h3 class="text-left">Third Party Recipients</h3>
                        <v-btn v-if="!readOnly" color="black" size="small" @click="createRecipient(1)">Add
                            Recipient
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-table height="200">
                            <thead>
                            <tr>
                                <th>Recipient</th>
                                <th>Sms</th>
                                <th>Email</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="user in third_party_users" v-if="third_party_users.length" :key="user.id"
                                class="text-left">
                                <td>{{ user?.email_enabled ? user.email : user.sms }}</td>
                                <td>
                                    <v-icon :icon="user.sms_enabled?'mdi-check':'mdi-close'"></v-icon>
                                </td>
                                <td>
                                    <v-icon :icon="user.email_enabled?'mdi-check':'mdi-close'"></v-icon>
                                </td>
                                <td>
                                    <v-btn-group v-if="!readOnly" density="compact" divided variant="outlined">
                                        <v-btn :disabled="editDialogSwitch" class="text-warning"
                                               prepend-icon="mdi-pencil"
                                               size="x-small" @click="editRecipient(user)"/>
                                        <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                               @click="deleteRecipient(user)"/>
                                    </v-btn-group>
                                    <span v-else class="text-grey">-</span>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="4">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else class="text-center" colspan="4">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button"
                       @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Create-Recipient v-if="createDialogSwitch" v-model="createDialogSwitch" :group="groupData" :type="recipientType"
                      @addRecipient="addRecipient"/>
    <Edit-Recipient v-if="editDialogSwitch" v-model="editDialogSwitch" :group="groupData" :recipient="selectedRecipient"
                    @updateRecipient="updateRecipient"/>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {ref, reactive, onMounted} from "vue";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import EditRecipient from "@/components/dialogs/monitoring/notifications/Edit.vue";
import CreateRecipient from "@/components/dialogs/monitoring/notifications/Create.vue";

const errors = ref([]);
const {user} = useAuthStore();
const loading = ref(false);
const recipientType = ref(0);
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const selectedRecipient = ref(null);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    company: {
        type: Number,
        default: null
    },
    group: {
        type: Object,
        default: null
    },
    readOnly: {
        type: Boolean,
        default: false
    },
});
const groupData = reactive({
    id: props.group?.id ?? null,
    label: props.group?.label ?? null,
    company_id: props.group?.company_id ?? ((user.type !== 'admin') ? user.company.id : props.company),
});
const emits = defineEmits(['update:modelValue', 'addGroup', 'updateGroup']);
const dialogSwitch = ref(props.modelValue);
const third_party_users = ref([]);
const users = ref([]);

onMounted(() => {
    if (groupData['id']) {
        fetchRecipients();
    }
})
const storeGroup = () => {
    errors.value = [];
    let update = groupData['id'] ?? false;
    send(`${update ? 'put' : 'post'}`, `monitoring/notification-groups${update ? `/${update}` : ''}`, groupData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                groupData['id'] = response.data.data.group.id;
                emits((update ? 'updateGroup' : 'addGroup'), response.data.data.group);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
const fetchRecipients = () => {
    loading.value = true;
    send('get', `monitoring/notification-groups/${groupData['id']}/members`)
        .then((response) => {
            users.value = response.data.data.members.users;
            third_party_users.value = response.data.data.members.third_party;
        })
        .catch((error) => {
            snackbar.showSnackbar('Recipients could not be loaded', 'error')
        }).finally(() => {
        loading.value = false
    })
}
const createRecipient = (type = 0) => {
    recipientType.value = type;
    createDialogSwitch.value = true;
}
const addRecipient = (recipient) => {
    if (recipient.type) {
        third_party_users.value.unshift(recipient);
    } else {
        users.value.unshift(recipient);
    }
}
const editRecipient = (recipient) => {
    selectedRecipient.value = recipient;
    editDialogSwitch.value = true;
}
const updateRecipient = (recipient) => {
    if (recipient.type) {
        third_party_users.value.map((recp, index) => {
            if (recp.id === recipient.id) {
                third_party_users.value[index] = recipient;
            }
        });
    } else {
        users.value.map((recp, index) => {
            if (recp.id === recipient.id) {
                users.value[index] = recipient;
            }
        });
    }
}
const deleteRecipient = (recipient) => {
    if (confirm(`Are you sure to delete selected recipient?`)) {
        send('delete', `monitoring/notification-group-members/${recipient.id}`, null)
            .then((response) => {
                if (response.data.success) {
                    if (recipient.type) {
                        third_party_users.value.map((item, index) => {
                            if (item.id === recipient.id) {
                                third_party_users.value.splice(index, 1);
                            }
                        });
                    } else {
                        users.value.map((item, index) => {
                            if (item.id === recipient.id) {
                                users.value.splice(index, 1);
                            }
                        });
                    }
                    snackbar.showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    snackbar.showSnackbar(error.response.data.message, 'error')
                } else {
                    snackbar.showSnackbar(`Recipient could not be deleted`, 'error')
                }
            })
    }
}
</script>
